/**
 * Scrolls to an element after waiting for the layout to stabilize.
 * Useful when content (like images) loads asynchronously and affects layout height.
 * 
 * @param selector - CSS selector for the target element
 * @param options - Configuration options
 * @returns Promise that resolves to true if scrolled successfully, false if element not found
 */
export function scrollToElementWhenStable(
    selector: string,
    options: {
        /** Scroll container element (default: use element's scrollIntoView) */
        container?: HTMLElement | null
        /** Scroll alignment (default: 'start') */
        block?: ScrollLogicalPosition
        /** Interval between stability checks in ms (default: 200) */
        checkInterval?: number
        /** Maximum number of attempts before giving up (default: 10) */
        maxAttempts?: number
        /** Initial delay before first check in ms (default: 100) */
        initialDelay?: number
        /** Enable debug logging (default: false) */
        debug?: boolean
    } = {}
): Promise<boolean> {
    const {
        container = null,
        block = 'start',
        checkInterval = 200,
        maxAttempts = 10,
        initialDelay = 100,
        debug = false
    } = options

    return new Promise((resolve) => {
        const el = document.querySelector(selector) as HTMLElement | null
        
        if (!el) {
            if (debug) console.warn(`scroll: ⚠️ Element not found for selector: ${selector}`)
            resolve(false)
            return
        }

        let attempt = 0
        let lastHeight = 0

        const tryScroll = () => {
            const currentHeight = el.offsetTop
            
            // Check if layout has stabilized (height hasn't changed)
            if (attempt > 0 && currentHeight === lastHeight) {
                if (container) {
                    // Scroll within container
                    const containerRect = container.getBoundingClientRect()
                    const elementRect = el.getBoundingClientRect()
                    container.scrollTop = elementRect.top - containerRect.top + container.scrollTop
                } else {
                    // Use native scrollIntoView
                    el.scrollIntoView({ block })
                }
                
                if (debug) {
                    console.log(`scroll: ✅ Scrolled to element after ${attempt} attempts (${selector})`)
                }
                resolve(true)
                return
            }

            lastHeight = currentHeight
            attempt++

            if (attempt >= maxAttempts) {
                // Give up and scroll anyway
                if (container) {
                    const containerRect = container.getBoundingClientRect()
                    const elementRect = el.getBoundingClientRect()
                    container.scrollTop = elementRect.top - containerRect.top + container.scrollTop
                } else {
                    el.scrollIntoView({ block })
                }

                if (debug) {
                    console.log(`scroll: ⚠️ Scrolled to element after max attempts (layout may be unstable) (${selector})`)
                }
                resolve(true)
                return
            }

            // Wait and try again
            setTimeout(tryScroll, checkInterval)
        }

        // Start first attempt after initial delay
        setTimeout(tryScroll, initialDelay)
    })
}

/**
 * Hook-compatible version that returns a callback function
 */
export function useScrollToElementWhenStable() {
    return scrollToElementWhenStable
}
